import memoize from "fast-memoize";
import memoizeOne from "memoize-one";
import {useContext, createContext} from "react";
import {matchPath} from "react-router-dom";
import shallowEqual from "shallowequal";
import type {History, Location, LocationDescriptorObject} from "history";
import {pronounceSafeSeq} from "@cdx/shared/sequences";
import {api} from "../../lib/api";
import {slugify} from "../../lib/utils";
import {waitForResultPromise} from "../../lib/wait-for-result";
import {cardPathToSeq} from "../../routes";
import {cardAccountSeqToCardId} from "../../lib/mate/cache";
import type {Card, CardId} from "../../cdx-models/Card";
import type {Milestone} from "../../cdx-models/Milestone";
import type {Deck} from "../../cdx-models/Deck";
import type {Sprint} from "../../cdx-models/Sprint";
import type {VisionBoard} from "../../cdx-models/VisionBoard";
import type {ResolvableId} from "../../cdx-models/Resolvable";
import type {ResolvableEntryId} from "../../cdx-models/ResolvableEntry";

// =============================================================================
// Card URL Options
// =============================================================================

// Valid side panel keys for card detail view
export type SidePanelKey = "props" | "attachments" | "comments" | "history" | "childCards";

// Card reference types - different route families need different card data
export type PathBasedCardRef = Pick<Card, "title" | "accountSeq">;
export type StateBasedCardRef = Pick<Card, "cardId">;

// Shared options for card URLs (without the card itself)
export type BaseCardUrlOpts = {
  targetPanel?: SidePanelKey;
  resolvableId?: ResolvableId;
  cardContainerKey?: string;
};

// Full card URL options per route family
export type PathBasedCardUrlOpts = BaseCardUrlOpts & {card: PathBasedCardRef};
export type StateBasedCardUrlOpts = BaseCardUrlOpts & {card: StateBasedCardRef};

// Union type for backwards compatibility
export type CardUrlOpts = PathBasedCardUrlOpts | StateBasedCardUrlOpts;

// Options for goToCard (always uses CardId, route implementation fetches what it needs)
export type GoToCardOpts = {
  cardId: CardId;
  targetPanel?: SidePanelKey;
  resolvableId?: ResolvableId;
  cardContainerKey?: string;
  replace?: boolean;
};

// =============================================================================
// Arena Routes
// =============================================================================

// Generic base interface for arena routes
interface BaseArenaRoutes<TCardUrlOpts extends BaseCardUrlOpts> {
  getActiveCard: (location: Location<any>) => Card | null;
  getCardUrl: (opts: TCardUrlOpts) => LocationDescriptorObject<any>;
  goToCard: (opts: GoToCardOpts) => void;
  rootPath: string;
  getRootUrl: () => string;
}

// Path-based routes: use URL path for card navigation (e.g., /milestone/123/card/abc)
export interface PathBasedArenaRoutes extends BaseArenaRoutes<PathBasedCardUrlOpts> {
  stateBased: false;
}

// State-based routes: use location state for card navigation
export interface StateBasedArenaRoutes extends BaseArenaRoutes<StateBasedCardUrlOpts> {
  stateBased: true;
}

// Union of all route types
export type ArenaRoutes = PathBasedArenaRoutes | StateBasedArenaRoutes;

// =============================================================================
// Arena Context Types
// =============================================================================

// Base properties shared by all arena contexts
interface BaseArenaCtx<TRoutes extends ArenaRoutes> {
  shortcutCategory: string;
  routes: TRoutes;
}

// Path-based arena contexts
export interface MilestoneArenaCtx extends BaseArenaCtx<PathBasedArenaRoutes> {
  type: "milestone";
  model: Milestone;
}

export interface SprintArenaCtx extends BaseArenaCtx<PathBasedArenaRoutes> {
  type: "sprint";
  model: Sprint;
}

export interface DeckArenaCtx extends BaseArenaCtx<PathBasedArenaRoutes> {
  type: "deck";
  model: Deck;
}

export interface DueDateArenaCtx extends BaseArenaCtx<PathBasedArenaRoutes> {
  type: "dueDate";
  model: null;
}

export interface HandQueueArenaCtx extends BaseArenaCtx<PathBasedArenaRoutes> {
  type: "handQueue";
  model: null;
}

// State-based arena contexts
export interface LibraryArenaCtx extends BaseArenaCtx<StateBasedArenaRoutes> {
  type: "library";
  model: null;
}

export interface VisionBoardArenaCtx extends BaseArenaCtx<StateBasedArenaRoutes> {
  type: "visionBoard";
  model: VisionBoard;
}

export interface UpvoteStatsArenaCtx extends BaseArenaCtx<StateBasedArenaRoutes> {
  type: "upvoteStats";
  model: null;
}

export interface ForecastArenaCtx extends BaseArenaCtx<StateBasedArenaRoutes> {
  type: "forecast";
  model: null;
}

// Union of all arena context types
export type PathBasedArenaCtx =
  | MilestoneArenaCtx
  | SprintArenaCtx
  | DeckArenaCtx
  | DueDateArenaCtx
  | HandQueueArenaCtx;

export type StateBasedArenaCtx =
  | LibraryArenaCtx
  | VisionBoardArenaCtx
  | UpvoteStatsArenaCtx
  | ForecastArenaCtx;

export type ArenaCtx = PathBasedArenaCtx | StateBasedArenaCtx;

// =============================================================================
// Utility Functions
// =============================================================================

export const toCardPath = ({title, accountSeq}: {title: string; accountSeq: number}) =>
  `${pronounceSafeSeq.intToSeq(accountSeq)}-${slugify(title)}`;

type CardUrlStateOpts = {
  targetPanel?: SidePanelKey;
  resolvableId?: ResolvableId;
  cardContainerKey?: string;
  latestSeenEntryId?: ResolvableEntryId;
};

export const cardUrlState = ({
  targetPanel,
  resolvableId,
  cardContainerKey,
  latestSeenEntryId,
}: CardUrlStateOpts) =>
  memoizedUrlState(targetPanel, resolvableId, cardContainerKey, latestSeenEntryId);

const memoizedUrlState = memoize(
  (
    targetPanel: SidePanelKey | undefined,
    resolvableId: ResolvableId | undefined,
    cardContainerKey: string | undefined,
    latestSeenEntryId: ResolvableEntryId | undefined
  ) => ({
    ...(targetPanel ? {sidePanelKey: targetPanel} : {}),
    ...(resolvableId ? {cardTargetResolvableId: resolvableId} : {}),
    ...(latestSeenEntryId !== undefined ? {cardLatestSeenEntryId: latestSeenEntryId} : {}),
    cardContainerKey,
  })
);

// =============================================================================
// Arena Context Factory
// =============================================================================

export const createArenaContext = memoizeOne(
  <T extends ArenaCtx>({type, shortcutCategory, model, routes}: T): T => {
    return {
      type,
      shortcutCategory,
      model,
      routes,
    } as T;
  },
  shallowEqual
);

// =============================================================================
// Route Factory Functions
// =============================================================================

type PathBasedRoutesOpts = {
  rootPath: string;
  getRootUrl: () => string;
  history: History;
};

export const getPathBasedRoutes = memoizeOne(
  ({rootPath, getRootUrl, history}: PathBasedRoutesOpts): PathBasedArenaRoutes => {
    const getCardUrl = ({
      card,
      targetPanel,
      resolvableId,
      cardContainerKey,
    }: PathBasedCardUrlOpts): LocationDescriptorObject => {
      const {accountSeq, title} = card as {accountSeq: number; title: string};
      return {
        pathname: `${getRootUrl()}/card/${toCardPath({accountSeq, title})}`,
        state: cardUrlState({targetPanel, resolvableId, cardContainerKey}),
      };
    };

    return {
      getActiveCard: (location) => {
        const cardMatch = matchPath<{cardId: string}>(location.pathname, {
          path: `${rootPath}/card/:cardId`,
        });
        if (cardMatch) {
          const accountSeq = cardPathToSeq(cardMatch.params.cardId);
          const cardId = cardAccountSeqToCardId.get(accountSeq);
          const card = cardId
            ? api.getModel({modelName: "card", id: cardId})
            : api.getRoot().account.$meta.find("cards", {accountSeq})[0] || null;
          return card;
        } else {
          return null;
        }
      },
      getCardUrl,
      goToCard: ({cardId, targetPanel, resolvableId, cardContainerKey, replace}) => {
        void waitForResultPromise(() => {
          const card = api.getModel({modelName: "card", id: cardId}) as Card;
          return {title: card.title, accountSeq: card.accountSeq};
        }).then(({title, accountSeq}) => {
          const url = getCardUrl({
            card: {title, accountSeq},
            targetPanel,
            resolvableId,
            cardContainerKey,
          });
          if (replace) {
            history.replace(url);
          } else {
            history.push(url);
          }
        });
      },
      rootPath: rootPath,
      getRootUrl,
      stateBased: false,
    };
  },
  shallowEqual
);

type StateBasedRoutesOpts = {
  rootPath: string;
  rootUrl: string;
  history: History;
};

export const getStateBasedRoutes = memoizeOne(
  ({rootPath, rootUrl, history}: StateBasedRoutesOpts): StateBasedArenaRoutes => {
    const getCardUrl = ({
      card,
      targetPanel,
      resolvableId,
      cardContainerKey,
    }: StateBasedCardUrlOpts): LocationDescriptorObject => ({
      pathname: rootUrl,
      state: {
        showCardId: card.cardId,
        ...cardUrlState({targetPanel, resolvableId, cardContainerKey}),
      },
    });

    return {
      getActiveCard: (location) => {
        if (location.state && location.state.showCardId) {
          return api.getModel({modelName: "card", id: location.state.showCardId});
        } else {
          return null;
        }
      },
      getCardUrl,
      goToCard: ({cardId, targetPanel, resolvableId, cardContainerKey, replace}) => {
        const url = getCardUrl({card: {cardId}, targetPanel, resolvableId, cardContainerKey});
        if (replace) {
          history.replace(url);
        } else {
          history.push(url);
        }
      },
      rootPath,
      getRootUrl: () => rootUrl,
      stateBased: true,
    };
  },
  shallowEqual
);

// =============================================================================
// React Context
// =============================================================================

const ArenaContext = createContext<ArenaCtx | null>(null);
export const ArenaContextProvider = ArenaContext.Provider;

export const useArenaContext = () => useContext(ArenaContext);
